import { useEffect, useId, useRef, useState } from 'react';

import { ApiError, api } from '@/api/client';
import { useLang } from '@/i18n/useLang';

export interface PlacePick {
  display_name: string;
  lat: number;
  lon: number;
}

interface PlacePickerProps {
  value: PlacePick | null;
  onChange: (p: PlacePick | null) => void;
  label?: string;
  placeholder?: string;
  inputClassName?: string;
  required?: boolean;
}

const DEFAULT_INPUT_CLASS =
  'rounded-md border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100 outline-none transition focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500';

export function PlacePicker({
  value,
  onChange,
  label,
  placeholder,
  inputClassName = DEFAULT_INPUT_CLASS,
  required,
}: PlacePickerProps) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [query, setQuery] = useState(value?.display_name ?? '');
  const [results, setResults] = useState<PlacePick[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  // While the input still shows the resolved place's display_name, we
  // skip re-searching (otherwise the dropdown pops back open on every
  // parent re-render). Cleared on any keystroke.
  const resolvedQueryRef = useRef<string | null>(value?.display_name ?? null);
  const listboxId = useId();

  useEffect(() => {
    if (resolvedQueryRef.current !== null && query === resolvedQueryRef.current) {
      return;
    }
    if (query.trim().length < 3) {
      setResults([]);
      setError(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.geocodeSearch(query, 5);
        if (cancelled) return;
        setResults(data.map((d) => ({ display_name: d.display_name, lat: d.lat, lon: d.lon })));
        setOpen(true);
        setActiveIdx(0);
      } catch (err) {
        if (cancelled) return;
        setResults([]);
        if (err instanceof ApiError && err.status === 404) {
          setError(t('कोई परिणाम नहीं', 'No matches'));
        } else {
          setError(t('खोज विफल', 'Search failed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, t]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const select = (pick: PlacePick) => {
    onChange(pick);
    resolvedQueryRef.current = pick.display_name;
    setQuery(pick.display_name);
    setResults([]);
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const p = results[activeIdx];
      if (p) select(p);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const labelText = label ?? t('जन्म स्थान', 'Place of birth');
  const ph = placeholder ?? t('जैसे: जयपुर, भारत', 'e.g. Jaipur, India');

  return (
    <div ref={containerRef} className="relative flex flex-col gap-1">
      <label
        htmlFor={`${listboxId}-input`}
        className="text-[11px] font-medium uppercase tracking-wider text-saffron-400"
      >
        {labelText}
      </label>
      <input
        id={`${listboxId}-input`}
        type="text"
        value={query}
        onChange={(e) => {
          resolvedQueryRef.current = null;
          setQuery(e.target.value);
          if (value !== null) onChange(null);
        }}
        onFocus={() => {
          if (resolvedQueryRef.current !== null) return;
          if (results.length > 0) setOpen(true);
        }}
        onKeyDown={onKey}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={open && results[activeIdx] ? `${listboxId}-opt-${activeIdx}` : undefined}
        placeholder={ph}
        autoComplete="off"
        required={required}
        className={inputClassName}
      />
      <div className="min-h-[1.1rem] text-[11px]">
        {loading && <span className="text-stone-500">{t('खोज रहे हैं…', 'Searching…')}</span>}
        {!loading && error && <span className="text-rose-400">{error}</span>}
        {!loading && !error && value && (
          <span className="text-emerald-400">
            ✓ ({value.lat.toFixed(2)}, {value.lon.toFixed(2)})
          </span>
        )}
      </div>
      {open && results.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-60 overflow-auto rounded-md border border-stone-700 bg-stone-950 shadow-2xl ring-1 ring-stone-800"
        >
          {results.map((r, i) => (
            <li
              key={`${r.lat}-${r.lon}-${i}`}
              id={`${listboxId}-opt-${i}`}
              role="option"
              aria-selected={i === activeIdx}
              onMouseDown={(e) => {
                e.preventDefault();
                select(r);
              }}
              onMouseEnter={() => setActiveIdx(i)}
              className={[
                'cursor-pointer px-3 py-2 text-sm',
                i === activeIdx
                  ? 'bg-saffron-700/40 text-stone-100'
                  : 'text-stone-300 hover:bg-stone-800',
              ].join(' ')}
            >
              <p className="line-clamp-2">{r.display_name}</p>
              <p className="text-[10px] text-stone-500">
                {r.lat.toFixed(4)}, {r.lon.toFixed(4)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
